/**
 * DataStore - MobX store for managing commit, PR, issue, and release data
 *
 * This store:
 * - Loads monthly data files from /data directory
 * - Stores commits, PRs, issues, and releases
 * - Provides computed values for easy data access
 * - Expands tuple data into more usable objects
 */

import { classFromScore } from "@getdevpulse/git-commit-complexity"
import { makeAutoObservable, runInAction } from "mobx"
import { fetchJson } from "../lib/fetch"
import { logger } from "../lib/logger"
import type {
  DataIndex,
  DataIndexLegacy,
  ExpandedCommit,
  ExpandedIssue,
  ExpandedPullRequest,
  ExpandedRelease,
  StoredMonthV4,
} from "../types/data"

const COMMIT_TYPE_ORDER = [
  "feature",
  "bugfix",
  "refactoring",
  "ci",
  "tests",
  "docs",
  "chore",
  "perf",
  "revert",
  "merge",
  "archive",
  "style",
  "other",
] as const

export class DataStore {
  // Loading state
  isLoading = false
  loadError: string | null = null

  // Available months
  availableMonths: string[] = []

  // File metadata with timestamps
  fileMetadata = new Map<string, { lastModified: number; filename: string }>()

  // Loaded month data (raw)
  monthlyData = new Map<string, StoredMonthV4>() // Authors mapping (shared across all months)
  private authorsMap = new Map<string, string>()

  constructor() {
    makeAutoObservable(this)
  }

  /**
   * Initialize the store by loading the data index
   */
  async initialize(): Promise<void> {
    if (this.isLoading) {
      logger.debug("DataStore already initializing, skipping", {
        component: "DataStore",
      })
      return
    }

    try {
      runInAction(() => {
        this.isLoading = true
        this.loadError = null
      })

      logger.info("Initializing DataStore...", { component: "DataStore" })
      logger.track("data_store_init_started")

      // Load the data index
      const dataIndexRaw = await fetchJson<DataIndex | DataIndexLegacy>("/data/data.json")

      // Handle both new format (with metadata) and legacy format (array of strings)
      let dataIndex: DataIndex
      if (Array.isArray(dataIndexRaw)) {
        // Legacy format: just an array of month strings
        logger.debug("Using legacy data.json format", {
          component: "DataStore",
        })
        dataIndex = {
          months: dataIndexRaw,
          files: dataIndexRaw.reduce(
            (acc, month) => {
              acc[month] = {
                lastModified: 0, // Unknown for legacy format
                filename: `${month}.json`,
              }
              return acc
            },
            {} as Record<string, { lastModified: number; filename: string }>,
          ),
        }
      } else {
        // New format with metadata
        logger.debug("Using new data.json format with timestamps", {
          component: "DataStore",
        })
        dataIndex = dataIndexRaw
      }

      runInAction(() => {
        this.availableMonths = dataIndex.months
        this.fileMetadata = new Map(Object.entries(dataIndex.files))
      })

      logger.info(`Found ${dataIndex.months.length} available months`, {
        component: "DataStore",
        months: dataIndex.months,
      })

      // Load all monthly data files
      await this.loadAllMonths()

      logger.info("DataStore initialized successfully", {
        component: "DataStore",
      })
      logger.track("data_store_init_success", {
        monthsCount: this.availableMonths.length,
        totalCommits: this.allCommits.length,
        totalPRs: this.allPullRequests.length,
      })
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to initialize DataStore"

      runInAction(() => {
        this.loadError = errorMessage
      })

      logger.error("Failed to initialize DataStore", error, {
        component: "DataStore",
      })
      logger.track("data_store_init_failed", { error: errorMessage })

      throw error
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  /**
   * Load all monthly data files
   */
  private async loadAllMonths(): Promise<void> {
    const loadPromises = this.availableMonths.map((month) => this.loadMonth(month))
    await Promise.all(loadPromises)
  }

  /**
   * Load a specific month's data
   */
  private async loadMonth(month: string): Promise<void> {
    try {
      logger.debug(`Loading month data: ${month}`, { component: "DataStore" })

      // Get file metadata for timestamp-based caching
      const metadata = this.fileMetadata.get(month)
      const lastModified = metadata?.lastModified || 0

      const monthData = await fetchJson<StoredMonthV4>(
        `/data/${month}.json`,
        undefined,
        lastModified,
      )

      runInAction(() => {
        this.monthlyData.set(month, monthData)

        // Merge authors into the global map
        for (const [id, handle] of Object.entries(monthData.authors)) {
          this.authorsMap.set(id, handle)
        }
      })

      logger.debug(`Successfully loaded month: ${month}`, {
        component: "DataStore",
        commits: monthData.commits?.length ?? 0,
        prs: monthData.prs?.length ?? 0,
      })
    } catch (error) {
      logger.error(`Failed to load month data: ${month}`, error, {
        component: "DataStore",
      })
      throw error
    }
  }

  /**
   * Get author handle by ID from a specific month's data
   * This is needed because author IDs are month-specific and can map to different handles
   */
  private getAuthorHandleForMonth(month: string, authorId: string): string {
    const monthData = this.monthlyData.get(month)
    if (monthData?.authors) {
      return monthData.authors[authorId] || authorId
    }
    return authorId
  }

  /**
   * Get repo name by index from a specific month
   */
  private getRepoName(month: string, repoIndex: number): string {
    const monthData = this.monthlyData.get(month)
    return monthData?.repos[repoIndex] || `repo-${repoIndex}`
  }

  /**
   * Get commit type string from ID
   */
  private getCommitType(typeId: number | undefined): string | undefined {
    if (typeId === undefined) return undefined
    return COMMIT_TYPE_ORDER[typeId]
  }

  /**
   * Computed: All commits from all months, expanded
   */
  get allCommits(): ExpandedCommit[] {
    const commits: ExpandedCommit[] = []

    for (const [month, monthData] of this.monthlyData.entries()) {
      for (const commitTuple of monthData.commits || []) {
        const [
          hash,
          repoIndex,
          authorId,
          timestamp,
          score,
          filesChanged,
          insertions,
          deletions,
          functionsChanged,
          cyclomaticDelta,
          maxNestingDelta,
          commitTypeId,
          message,
        ] = commitTuple

        commits.push({
          hash,
          repoIndex,
          repoName: this.getRepoName(month, repoIndex),
          authorId,
          authorHandle: this.getAuthorHandleForMonth(month, authorId),
          timestamp,
          timestampDate: new Date(timestamp * 1000),
          score,
          complexityClass: classFromScore(score),
          filesChanged,
          insertions,
          deletions,
          functionsChanged,
          cyclomaticDelta,
          maxNestingDelta,
          commitType: this.getCommitType(commitTypeId),
          message,
        })
      }
    }

    return commits
  }

  /**
   * Computed: All pull requests from all months, expanded
   */
  get allPullRequests(): ExpandedPullRequest[] {
    const prs: ExpandedPullRequest[] = []

    for (const [month, monthData] of this.monthlyData.entries()) {
      for (const prTuple of monthData.prs || []) {
        const [
          id,
          repoIndex,
          number,
          title,
          creatorId,
          createdAt,
          mergedAt,
          closedAt,
          state,
          draft,
          additions,
          deletions,
          changedFiles,
          comments,
        ] = prTuple

        prs.push({
          id,
          repoIndex,
          repoName: this.getRepoName(month, repoIndex),
          number,
          title,
          creatorId,
          creatorHandle: this.getAuthorHandleForMonth(month, creatorId),
          createdAt,
          createdAtDate: new Date(createdAt * 1000),
          mergedAt,
          mergedAtDate: mergedAt ? new Date(mergedAt * 1000) : undefined,
          closedAt,
          closedAtDate: closedAt ? new Date(closedAt * 1000) : undefined,
          state,
          draft,
          additions,
          deletions,
          changedFiles,
          comments,
        })
      }
    }

    return prs
  }

  /**
   * Computed: All issues from all months, expanded
   */
  get allIssues(): ExpandedIssue[] {
    const issues: ExpandedIssue[] = []

    for (const [month, monthData] of this.monthlyData.entries()) {
      for (const issueTuple of monthData.issues || []) {
        const [
          id,
          repoIndex,
          number,
          title,
          creatorId,
          createdAt,
          closedAt,
          closedById,
          state,
          comments,
          commentAuthors,
          firstCommentAt,
          lastCommentAt,
        ] = issueTuple

        issues.push({
          id,
          repoIndex,
          repoName: this.getRepoName(month, repoIndex),
          number,
          title,
          creatorId,
          creatorHandle: this.getAuthorHandleForMonth(month, creatorId),
          createdAt,
          createdAtDate: new Date(createdAt * 1000),
          closedAt,
          closedAtDate: closedAt ? new Date(closedAt * 1000) : undefined,
          closedById,
          closedByHandle: closedById ? this.getAuthorHandleForMonth(month, closedById) : undefined,
          state,
          comments,
          commentAuthors,
          firstCommentAt,
          firstCommentAtDate: firstCommentAt ? new Date(firstCommentAt * 1000) : undefined,
          lastCommentAt,
          lastCommentAtDate: lastCommentAt ? new Date(lastCommentAt * 1000) : undefined,
        })
      }
    }

    return issues
  }

  /**
   * Computed: All releases from all months, expanded
   */
  get allReleases(): ExpandedRelease[] {
    const releases: ExpandedRelease[] = []

    for (const [month, monthData] of this.monthlyData.entries()) {
      for (const releaseTuple of monthData.releases || []) {
        const [id, repoIndex, tagName, name, authorId, createdAt, publishedAt, draft, prerelease] =
          releaseTuple

        releases.push({
          id,
          repoIndex,
          repoName: this.getRepoName(month, repoIndex),
          tagName,
          name,
          authorId,
          authorHandle: this.getAuthorHandleForMonth(month, authorId),
          createdAt,
          createdAtDate: new Date(createdAt * 1000),
          publishedAt,
          publishedAtDate: publishedAt ? new Date(publishedAt * 1000) : undefined,
          draft,
          prerelease,
        })
      }
    }

    return releases
  }

  /**
   * Get commits for a specific month
   */
  getCommitsByMonth(month: string): ExpandedCommit[] {
    const monthData = this.monthlyData.get(month)
    if (!monthData) return []

    return (monthData.commits || []).map((commitTuple) => {
      const [
        hash,
        repoIndex,
        authorId,
        timestamp,
        score,
        filesChanged,
        insertions,
        deletions,
        functionsChanged,
        cyclomaticDelta,
        maxNestingDelta,
        commitTypeId,
        message,
      ] = commitTuple

      return {
        hash,
        repoIndex,
        repoName: this.getRepoName(month, repoIndex),
        authorId,
        authorHandle: this.getAuthorHandleForMonth(month, authorId),
        timestamp,
        timestampDate: new Date(timestamp * 1000),
        score,
        complexityClass: classFromScore(score),
        filesChanged,
        insertions,
        deletions,
        functionsChanged,
        cyclomaticDelta,
        maxNestingDelta,
        commitType: this.getCommitType(commitTypeId),
        message,
      }
    })
  }

  /**
   * Get commits by author
   */
  getCommitsByAuthor(authorId: string): ExpandedCommit[] {
    return this.allCommits.filter((commit) => commit.authorId === authorId)
  }

  /**
   * Get commits by repo
   */
  getCommitsByRepo(repoName: string): ExpandedCommit[] {
    return this.allCommits.filter((commit) => commit.repoName === repoName)
  }

  /**
   * Get all unique repositories
   */
  get allRepositories(): string[] {
    const repos = new Set<string>()

    for (const monthData of this.monthlyData.values()) {
      for (const repo of monthData.repos) {
        repos.add(repo)
      }
    }

    return Array.from(repos).sort()
  }

  /**
   * Get all unique authors
   * Deduplicates by handle and includes all contributors from commits, PRs, and issues
   * This ensures the dropdown includes all active contributors, not just those in metadata
   */
  get allAuthors(): Array<{ id: string; handle: string }> {
    const uniqueHandles = new Map<string, string>()

    // First, add all authors from the metadata authorsMap
    for (const [id, handle] of this.authorsMap.entries()) {
      if (!uniqueHandles.has(handle)) {
        uniqueHandles.set(handle, id)
      }
    }

    // Then, add all authors from commits (in case some are missing from metadata)
    for (const commit of this.allCommits) {
      const handle = commit.authorHandle
      if (handle && !uniqueHandles.has(handle)) {
        uniqueHandles.set(handle, commit.authorId)
      }
    }

    // Add all PR creators
    for (const pr of this.allPullRequests) {
      const handle = pr.creatorHandle
      if (handle && !uniqueHandles.has(handle)) {
        // Use handle as id if not in metadata
        uniqueHandles.set(handle, handle)
      }
    }

    // Add all issue creators and closers
    for (const issue of this.allIssues) {
      if (issue.creatorHandle && !uniqueHandles.has(issue.creatorHandle)) {
        uniqueHandles.set(issue.creatorHandle, issue.creatorHandle)
      }
      if (issue.closedByHandle && !uniqueHandles.has(issue.closedByHandle)) {
        uniqueHandles.set(issue.closedByHandle, issue.closedByHandle)
      }
    }

    return Array.from(uniqueHandles.entries())
      .map(([handle, id]) => ({ id, handle }))
      .sort((a, b) => a.handle.localeCompare(b.handle))
  }

  /**
   * Get reverse mapping from handle to ID
   */
  get handleToIdMap(): Map<string, string> {
    const map = new Map<string, string>()
    for (const [id, handle] of this.authorsMap.entries()) {
      map.set(handle, id)
    }
    return map
  }

  /**
   * Reset the store
   */
  reset(): void {
    this.isLoading = false
    this.loadError = null
    this.availableMonths = []
    this.monthlyData.clear()
    this.authorsMap.clear()
    logger.info("DataStore reset", { component: "DataStore" })
  }
}
